import { useState } from 'react';
import { type FieldValues, useController } from 'react-hook-form';
import { TextField } from '../tailgrids/core/text-field.js';
import { Input } from '../tailgrids/core/input.js';
import { TextArea } from '../tailgrids/core/text-area.js';
import { cn } from '../../utils/cn.js';
import { type EmptyAs, FormDescription, FormFieldError, type FormFieldBaseProps, FormLabel } from './field-parts.js';

const FIELD_CLASS = 'w-full flex-col gap-1.5';

/** The input's text for a form value: absent values show as an empty input. */
const asText = (value: unknown): string => (value === undefined || value === null ? '' : String(value));

export interface TextFormFieldProps<TValues extends FieldValues = FieldValues> extends FormFieldBaseProps<TValues> {
  type?: 'text' | 'email' | 'password' | 'url' | 'search' | 'tel';
  placeholder?: string;
  autoComplete?: string;
  readOnly?: boolean;
  /** What clearing the input writes: unset by default, so an optional field stays optional. */
  emptyAs?: EmptyAs;
}

export function TextFormField<TValues extends FieldValues = FieldValues>({
  name,
  label,
  description,
  help,
  required,
  disabled,
  className,
  type = 'text',
  placeholder,
  autoComplete,
  readOnly,
  emptyAs,
}: TextFormFieldProps<TValues>) {
  const { field, fieldState } = useController<TValues>({ name });
  return (
    <TextField
      className={cn(FIELD_CLASS, className)}
      name={field.name}
      type={type}
      value={asText(field.value)}
      onChange={(text) => field.onChange(text === '' ? emptyAs : text)}
      onBlur={field.onBlur}
      invalid={fieldState.invalid}
      required={required}
      disabled={disabled || field.disabled}
      readOnly={readOnly}
      autoComplete={autoComplete}
      validationBehavior="aria"
    >
      <FormLabel label={label} required={required} help={help} />
      <Input ref={field.ref} placeholder={placeholder} className="w-full px-3 py-2.5 text-sm" />
      <FormDescription>{description}</FormDescription>
      <FormFieldError message={fieldState.error?.message} />
    </TextField>
  );
}

export interface TextAreaFormFieldProps<TValues extends FieldValues = FieldValues> extends FormFieldBaseProps<TValues> {
  placeholder?: string;
  rows?: number;
  readOnly?: boolean;
  emptyAs?: EmptyAs;
}

export function TextAreaFormField<TValues extends FieldValues = FieldValues>({
  name,
  label,
  description,
  help,
  required,
  disabled,
  className,
  placeholder,
  rows = 4,
  readOnly,
  emptyAs,
}: TextAreaFormFieldProps<TValues>) {
  const { field, fieldState } = useController<TValues>({ name });
  return (
    <TextField
      className={cn(FIELD_CLASS, className)}
      name={field.name}
      value={asText(field.value)}
      onChange={(text) => field.onChange(text === '' ? emptyAs : text)}
      onBlur={field.onBlur}
      invalid={fieldState.invalid}
      required={required}
      disabled={disabled || field.disabled}
      readOnly={readOnly}
      validationBehavior="aria"
    >
      <FormLabel label={label} required={required} help={help} />
      <TextArea ref={field.ref} rows={rows} placeholder={placeholder} className="w-full px-3 py-2.5 text-sm" />
      <FormDescription>{description}</FormDescription>
      <FormFieldError message={fieldState.error?.message} />
    </TextField>
  );
}

export interface JsonFormFieldProps<TValues extends FieldValues = FieldValues> extends FormFieldBaseProps<TValues> {
  placeholder?: string;
  rows?: number;
}

/**
 * Edits an object-valued field as JSON text. The form holds the parsed object (what the API takes).
 * Text that does not parse is reported on the field and fails the schema, so it never reaches the
 * server.
 */
export function JsonFormField<TValues extends FieldValues = FieldValues>({
  name,
  label,
  description,
  help,
  required,
  disabled,
  className,
  placeholder = '{ }',
  rows = 8,
}: JsonFormFieldProps<TValues>) {
  const { field, fieldState } = useController<TValues>({ name });
  const [text, setText] = useState(() => (field.value === undefined ? '' : JSON.stringify(field.value, null, 2)));
  const [parseError, setParseError] = useState<string | null>(null);

  const onChange = (next: string) => {
    setText(next);
    if (next.trim() === '') {
      setParseError(null);
      field.onChange(undefined);
      return;
    }
    try {
      field.onChange(JSON.parse(next));
      setParseError(null);
    } catch {
      // Hand the raw text to the form: the object schema rejects it, so the submit is blocked rather
      // than sending the last valid value.
      field.onChange(next);
      setParseError('Not valid JSON — check commas, quotes and brackets');
    }
  };

  const message = parseError ?? fieldState.error?.message;
  return (
    <TextField
      className={cn(FIELD_CLASS, className)}
      name={field.name}
      value={text}
      onChange={onChange}
      onBlur={field.onBlur}
      invalid={message !== undefined}
      required={required}
      disabled={disabled || field.disabled}
      validationBehavior="aria"
    >
      <FormLabel label={label} required={required} help={help} />
      <TextArea
        ref={field.ref}
        rows={rows}
        spellCheck={false}
        placeholder={placeholder}
        className="w-full px-3 py-2.5 font-mono text-xs"
      />
      <FormDescription>{description}</FormDescription>
      <FormFieldError message={message ?? undefined} />
    </TextField>
  );
}
